import React, { useState, useEffect } from 'react'
import { Button, Popover } from '@mui/material'
import Calendar from 'react-calendar'
import dayjs from 'dayjs'
import 'react-calendar/dist/Calendar.css'
import { DayJsDateSelectorCalendarStyleContainer } from './styledComponents'

const DayjsDatePicker = ({
  date: dayjsDate,
  isDisabled = false,
  onChange,
  format = 'DD-MMM-YYYY',
  placeholder = 'Select a date...',
  extraHandlingAfterOnClick = () => {},
  buttonProps = {},
  ButtonComponent = Button,
  calendarProps = {},
  buttonTextOverride = null, // Custom button text override
}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [calendarView, setCalendarView] = useState('month')

  // Open the popover by setting the anchor element (the button)
  const handleButtonClick = (event) => {
    setAnchorEl(event.currentTarget)
    // When date is unset, start from year view (which shows months) instead of month view (which shows days)
    if (!dayjsDate) {
      setCalendarView('year')
    } else {
      setCalendarView('month') // Use month view when date is set
    }
    extraHandlingAfterOnClick()
  }

  // Close the popover
  const handleClose = () => {
    setAnchorEl(null)
  }

  // When a date is selected in the calendar
  const handleDateSelect = (date) => {
    // Convert the selected Date object to a dayjs object
    const newDayjsDate = dayjs(date)
    // Call the provided onChange callback
    onChange(newDayjsDate)
    // Close the popover
    handleClose()
  }

  // Determine if the popover is open
  const open = Boolean(anchorEl)
  const id = open ? 'dayjs-date-picker-popover' : undefined

  // Ensure we have a Date object for react-calendar
  const selectedDate = dayjsDate ? dayjs(dayjsDate).toDate() : new Date()

  // Reset calendar view when popover closes
  useEffect(() => {
    if (!open) {
      setCalendarView('month')
    }
  }, [open])

  // Handle view change from react-calendar
  // react-calendar's onViewChange can pass either a string or an object
  const handleViewChange = (nextView) => {
    // Extract view string if it's an object, otherwise use the value directly
    const viewString = typeof nextView === 'string' 
      ? nextView 
      : (nextView?.view || 'month')
    
    // Ensure it's a valid view value
    if (['century', 'decade', 'year', 'month'].includes(viewString)) {
      setCalendarView(viewString)
    } else {
      setCalendarView('month') // Default fallback
    }
  }

  // Filter out 'view' and 'onViewChange' from calendarProps to avoid conflicts
  // These are controlled by our internal state
  const { view: _ignoredView, onViewChange: _ignoredOnViewChange, ...safeCalendarProps } = calendarProps || {}

  return (
    <>
      <ButtonComponent
        disabled={isDisabled}
        variant="outlined"
        onClick={handleButtonClick}
        {...buttonProps}
      >
        {buttonTextOverride !== null 
          ? buttonTextOverride 
          : (dayjsDate ? dayjs(dayjsDate).format(format) : placeholder)}
      </ButtonComponent>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        container={() => document.body}
        disablePortal={false}
        sx={{
          zIndex: 30000,
        }}
        PaperProps={{
          style: {
            zIndex: '30000 !important',
            backgroundColor: '#ffffff',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            borderRadius: '8px',
            border: '1px solid rgba(0, 0, 0, 0.1)',
            overflow: 'visible',
            marginTop: '4px',
            position: 'relative',
          },
          sx: {
            zIndex: 30000,
          },
        }}
        slotProps={{
          root: {
            sx: {
              zIndex: 30000,
            },
          },
        }}
      >
        <DayJsDateSelectorCalendarStyleContainer>
          <Calendar 
            onChange={handleDateSelect} 
            value={selectedDate} 
            view={calendarView}
            onViewChange={handleViewChange}
            {...safeCalendarProps} 
          />
        </DayJsDateSelectorCalendarStyleContainer>
      </Popover>
    </>
  )
}

export default DayjsDatePicker

